import { Button } from "common/components/ui/Button";
import { DialogDescription, DialogTitle } from "common/components/ui/Dialog";
import { DrawerDescription, DrawerTitle } from "common/components/ui/Drawer";
import ProfileImage from "common/components/ui/ProfileImage";
import { Tag } from "common/components/ui/Tag";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { WorkspaceAccount } from "common/types";

interface AssignSeatsProps {
  accounts: WorkspaceAccount[];
  onClose: () => void;
  onSuccess?: () => void;
}

export default function AssignSeats({
  accounts,
  onClose,
  onSuccess = undefined,
}: AssignSeatsProps) {
  const isDesktop = useTwBreakpoint("lg");

  const { assignLicenses, isAssigningLicenses } = useLicenseMutations();

  async function handleAssignLicenses() {
    await assignLicenses({ accountIds: accounts.map(({ id }) => id) });

    if (onSuccess) {
      onSuccess();
    }
  }

  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <>
      <Title>Assign seats to campaign accounts?</Title>
      <Description className="mb-4">
        You need to assign seats in order to start this campaign
      </Description>

      <div className="my-4 flex max-h-[min(378px,60dvh)] flex-col gap-y-3 overflow-y-auto px-2 scrollbar-thin">
        {accounts.map(({ id, picture_url, full_name, occupation }) => (
          <div
            key={id}
            className="flex items-center gap-x-2 rounded-3xl border border-black-200 p-3"
          >
            <ProfileImage src={picture_url} />

            <div className="flex flex-col gap-y-0.5">
              <div className="flex items-center gap-x-1">
                <h4 className="line-clamp-1 break-all text-sm-bold">
                  {full_name}
                </h4>

                <Tag variant="tertiary" size="sm">
                  No assigned seat
                </Tag>
              </div>

              <p className="line-clamp-1 break-all text-xs-medium text-black-500">
                {occupation}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-col gap-y-2 lg:flex-row-reverse lg:gap-x-4">
        <Button
          size={isDesktop ? "md" : "lg"}
          className="flex-1"
          variant="gradient"
          onClick={handleAssignLicenses}
          isLoading={isAssigningLicenses}
        >
          Assign
        </Button>
        <Button
          variant="secondary-black"
          size={isDesktop ? "md" : "lg"}
          className="flex-1"
          disabled={isAssigningLicenses}
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </>
  );
}
